import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" style={{ animation:'spin 0.7s linear infinite', flexShrink:0 }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

export default function VerifyEmailPage() {
  const navigate  = useNavigate();
  // AuthContext available if needed

  // Email passed from login page via location state, or from localStorage fallback
  const [email,     setEmail]     = useState(() => {
    try { return window.history.state?.usr?.email || localStorage.getItem('dayrit_pending_email') || ''; }
    catch { return ''; }
  });
  const [token,     setToken]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [resending, setResending] = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    if (!token.trim()) { setError('Please enter the verification code.'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/auth/verify-email', { email: email.trim(), token: token.trim() });
      const data = res.data;

      // Already verified OR freshly verified — both return a token, log in directly
      if (data?.token) {
        localStorage.setItem('dayrit_token', data.token);
        localStorage.removeItem('dayrit_pending_email');
        setSuccess(true);
        setTimeout(() => window.location.replace('/app'), 1200);
        return;
      }

      // Fallback: no token returned, go to login
      setSuccess(true);
      localStorage.removeItem('dayrit_pending_email');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      const msg = err.response?.data?.message;
      // Map validation errors to friendly messages
      if (err.response?.status === 422) {
        setError(msg || 'Invalid or expired code. Please check and try again.');
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || resending) return;
    setResending(true); setResendMsg(''); setError('');
    try {
      await api.post('/auth/resend-verification', { email });
      setResendMsg('New code sent — check your inbox.');
      setCountdown(60);
    } catch (err) {
      if (err.response?.status === 429) {
        setResendMsg('Please wait before requesting another code.');
        setCountdown(60);
      } else {
        setError(err.response?.data?.message || 'Could not resend. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:'clamp(16px,4vw,40px)', fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif" }}>
      <style>{`
        @keyframes spin    { to { transform:rotate(360deg) } }
        @keyframes fadeIn  { from { opacity:0;transform:translateY(6px) } to { opacity:1;transform:none } }
        @keyframes popIn   { from { opacity:0;transform:scale(0.85) } to { opacity:1;transform:scale(1) } }
      `}</style>

      <div style={{ width:'100%', maxWidth:400, animation:'fadeIn 0.3s ease' }}>

        {/* Wordmark */}
        <Link to="/" style={{ display:'block', textAlign:'center', marginBottom:32, textDecoration:'none' }}>
          <span style={{ fontSize:22, fontWeight:900, letterSpacing:'-0.6px', background:'var(--brand-grad)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Dayrit</span>
        </Link>

        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, padding:'clamp(24px,5vw,36px)', boxShadow:'0 4px 24px rgba(0,0,0,0.06)' }}>

          {/* ── Success state ── */}
          {success ? (
            <div style={{ textAlign:'center', animation:'popIn 0.4s ease' }}>
              <div style={{ width:68, height:68, borderRadius:'50%', margin:'0 auto 18px', background:'linear-gradient(135deg,#16A34A,#4ade80)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 8px 24px rgba(22,163,74,0.3)' }}>
                <svg width="30" height="24" viewBox="0 0 30 24" fill="none">
                  <path d="M2 12L10 20L28 2" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 style={{ fontSize:21, fontWeight:800, color:'var(--text)', letterSpacing:'-0.4px', marginBottom:8 }}>Email verified!</h2>
              <p style={{ fontSize:14, color:'var(--text3)', lineHeight:1.6 }}>Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ textAlign:'center', marginBottom:26 }}>
                <div style={{ fontSize:44, marginBottom:12, lineHeight:1 }}>📬</div>
                <h1 style={{ fontSize:21, fontWeight:800, color:'var(--text)', letterSpacing:'-0.4px', marginBottom:8 }}>Check your inbox</h1>
                <p style={{ fontSize:14, color:'var(--text3)', lineHeight:1.6 }}>
                  We sent a verification code to<br/>
                  <strong style={{ color:'var(--text)' }}>{email || 'your email'}</strong>
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleVerify} style={{ display:'flex', flexDirection:'column', gap:14 }}>

                {/* Email field — editable in case it came in wrong */}
                <div>
                  <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text2)', marginBottom:5 }}>Email address</label>
                  <input
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    style={{ width:'100%', boxSizing:'border-box', padding:'10px 13px', fontSize:14, fontFamily:'inherit', border:'1.5px solid var(--border)', borderRadius:10, background:'var(--input-bg)', color:'var(--text)', outline:'none' }}
                  />
                </div>

                {/* Token field */}
                <div>
                  <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text2)', marginBottom:5 }}>Verification code</label>
                  <input
                    type="text" value={token} onChange={e => setToken(e.target.value)}
                    placeholder="Paste code from email" autoFocus autoComplete="one-time-code"
                    style={{ width:'100%', boxSizing:'border-box', padding:'10px 13px', fontSize:14, fontFamily:'inherit', border:`1.5px solid ${error ? '#EF4444' : 'var(--border)'}`, borderRadius:10, background:'var(--input-bg)', color:'var(--text)', outline:'none', letterSpacing:'1px' }}
                  />
                  <p style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>Can't find it? Check your spam folder.</p>
                </div>

                {/* Error */}
                {error && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px', borderRadius:9, background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.2)', fontSize:13, color:'#DC2626', fontWeight:500 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink:0 }}>
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    {error}
                  </div>
                )}

                {/* Resend success */}
                {resendMsg && !error && (
                  <div style={{ padding:'10px 12px', borderRadius:9, background:'rgba(22,163,74,0.07)', border:'1px solid rgba(22,163,74,0.2)', fontSize:13, color:'#16A34A', fontWeight:500 }}>
                    ✓ {resendMsg}
                  </div>
                )}

                {/* Submit */}
                <button type="submit" disabled={loading || !token.trim()} style={{
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  width:'100%', padding:'13px', borderRadius:11, border:'none',
                  background: (loading || !token.trim()) ? 'var(--border)' : 'var(--brand-grad)',
                  color: (loading || !token.trim()) ? 'var(--text3)' : 'white',
                  fontSize:15, fontWeight:700, cursor:(loading || !token.trim()) ? 'not-allowed' : 'pointer',
                  fontFamily:'inherit', minHeight:48, transition:'all 0.15s',
                  boxShadow: (!loading && token.trim()) ? '0 4px 16px var(--accent-ring)' : 'none',
                }}>
                  {loading ? <><Spinner/>Verifying…</> : 'Verify email →'}
                </button>

              </form>

              {/* Resend */}
              <div style={{ marginTop:18, textAlign:'center' }}>
                <p style={{ fontSize:13, color:'var(--text3)' }}>
                  Didn't receive it?{' '}
                  <button onClick={handleResend} disabled={countdown > 0 || resending}
                    style={{ background:'none', border:'none', cursor: countdown > 0 ? 'not-allowed' : 'pointer', color: countdown > 0 ? 'var(--text3)' : 'var(--accent)', fontWeight:700, fontSize:13, fontFamily:'inherit', padding:0 }}>
                    {resending ? 'Sending…' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
                  </button>
                </p>
              </div>
            </>
          )}
        </div>

        <p style={{ textAlign:'center', marginTop:18, fontSize:13, color:'var(--text3)' }}>
          Wrong account?{' '}
          <Link to="/login" style={{ color:'var(--accent)', fontWeight:700, textDecoration:'none' }}>Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}